import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCategoryParam } from '../../hooks/useCategoryParam';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Plus, Minus, Clock, CalendarDays, ChevronRight, Sparkles, Play, Instagram, Youtube, Facebook, Tag } from 'lucide-react';
import axios from 'axios';
import InstamartHome from './InstamartHome';
import ZeptoHome from './ZeptoHome';
import BlinkitHome from './BlinkitHome';
import CountryHome from './CountryHome';
import { PromoBannerCarousel } from '../../components/customer/PromoBannerCarousel';
import { CouponStrip } from '../../components/customer/CouponStrip';
import { UnavailableBadge, isUnavailable, isVariantUnavailable } from '../../components/customer/UnavailableBadge';
import { ProductImageCarousel } from '../../components/customer/ProductImageCarousel';
import { SubCategoryFilter } from '../../components/customer/SubCategoryFilter';
import { ProductCard } from '../../components/customer/ProductCard';
import { PRODUCT_GRID } from '../../components/customer/customerLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Video Embed Helpers =====
const getYouTubeEmbedUrl = (url) => {
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/shorts\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=0&mute=1&loop=1`;
  }
  return null;
};

const getInstagramEmbedUrl = (url) => {
  // Instagram embeds need oEmbed or blockquote approach
  const m = url.match(/instagram\.com\/(reel|p)\/([^/?]+)/);
  if (m) return `https://www.instagram.com/${m[1]}/${m[2]}/embed`;
  return null;
};

const VideoBannerCarousel = ({ banners }) => {
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef(null);

  if (!banners || banners.length === 0) return null;

  const platformIcon = (p) => {
    if (p === 'instagram') return <Instagram className="w-3.5 h-3.5" />;
    if (p === 'youtube') return <Youtube className="w-3.5 h-3.5" />;
    if (p === 'facebook') return <Facebook className="w-3.5 h-3.5" />;
    return <Play className="w-3.5 h-3.5" />;
  };

  return (
    <div className="space-y-3" data-testid="video-banners-section">
      <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)] flex items-center gap-2">
        <Play className="w-4 h-4" /> Watch & Shop
      </h3>
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        {banners.map((banner, i) => {
          let embedUrl = null;
          if (banner.platform === 'youtube') embedUrl = getYouTubeEmbedUrl(banner.url);
          else if (banner.platform === 'instagram') embedUrl = getInstagramEmbedUrl(banner.url);

          return (
            <div key={banner.id} className="snap-start shrink-0 w-52 bg-white rounded-2xl border overflow-hidden" data-testid={`video-banner-${i}`}>
              {embedUrl ? (
                <div className="relative w-full" style={{ paddingBottom: '177%' }}>
                  <iframe
                    src={embedUrl}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    loading="lazy"
                    title={banner.title || `Video ${i + 1}`}
                  />
                </div>
              ) : (
                <a href={banner.url} target="_blank" rel="noreferrer" className="block">
                  <div className="bg-gray-900 w-full flex items-center justify-center" style={{ paddingBottom: '177%', position: 'relative' }}>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      <Play className="w-10 h-10 mb-2 opacity-80" />
                      <span className="text-xs opacity-60">Tap to watch</span>
                    </div>
                  </div>
                </a>
              )}
              {banner.title && (
                <div className="p-2 flex items-center gap-1.5">
                  <span className="text-gray-400">{platformIcon(banner.platform)}</span>
                  <p className="text-xs text-gray-700 truncate">{banner.title}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CustomerHome = () => {
  const { user, storeSettings } = useAuth();

  // Route to theme-specific layout if applicable
  if (storeSettings?.theme === 'theme-instamart' || storeSettings?.theme === 'theme-modern-organic') {
    return <InstamartHome />;
  }
  if (storeSettings?.theme === 'theme-zepto') {
    return <ZeptoHome />;
  }
  if (storeSettings?.theme === 'theme-blinkit') {
    return <BlinkitHome />;
  }
  if (storeSettings?.theme === 'theme-country') {
    return <CountryHome />;
  }

  // Fallback — use Instamart layout as default
  return <InstamartHome />;
};

const DefaultHome = () => {
  const navigate = useNavigate();
  const { user, storeSettings } = useAuth();
  const { addItem, getItemQuantity, updateQuantity } = useCart();
  
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  // In the URL, not local state, so Back from a product returns to this category.
  const [selectedCategory, setSelectedCategory] = useCategoryParam();
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socialData, setSocialData] = useState(null);
  const [promoBanners, setPromoBanners] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const sessionId = useRef(sessionStorage.getItem('_sid') || (() => { const s = Math.random().toString(36).slice(2); sessionStorage.setItem('_sid', s); return s; })());

  useEffect(() => {
    loadData();
    trackPageview();
  }, [storeSettings?.admin_id]);

  const loadData = async () => {
    try {
      const adminId = user?.admin_id || storeSettings?.admin_id;
      let reqs;
      if (user && user.admin_id) {
        reqs = [
          axios.get(`${API}/admin/categories`),
          axios.get(`${API}/customer/products`),
          axios.get(`${API}/admin/delivery-slots`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${user.admin_id}/social`).catch(() => ({ data: null })),
          axios.get(`${API}/store/${user.admin_id}/promo-banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${user.admin_id}/coupons`).catch(() => ({ data: [] })),
        ];
      } else if (adminId) {
        reqs = [
          axios.get(`${API}/public/store/${adminId}/categories`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/store/${adminId}/products`).catch(() => ({ data: [] })),
          Promise.resolve({ data: [] }),
          Promise.resolve({ data: null }),
          axios.get(`${API}/store/${adminId}/promo-banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${adminId}/coupons`).catch(() => ({ data: [] })),
        ];
      } else {
        setLoading(false);
        return;
      }

      const results = await Promise.all(reqs);
      setCategories(results[0].data);
      setProducts(results[1].data);
      setDeliverySlots(results[2].data || []);
      if (results[3]?.data) setSocialData(results[3].data);
      if (results[4]) setPromoBanners(results[4].data);
      if (results[5]) setCoupons(results[5].data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackPageview = () => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/pageview`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, page: '/home', source: detectSource(),
    }).catch(() => {});
  };

  const trackEvent = (eventType, extra = {}) => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/event`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, event_type: eventType, ...extra,
    }).catch(() => {});
  };

  const detectSource = () => {
    const ref = document.referrer || '';
    if (ref.includes('instagram')) return 'instagram';
    if (ref.includes('facebook') || ref.includes('fb.')) return 'facebook';
    if (ref.includes('google')) return 'google';
    if (ref.includes('youtube')) return 'youtube';
    return ref ? 'referral' : 'direct';
  };

  const handleSelectCategory = (catId) => {
    setSelectedCategory(catId);
    setSelectedSubCategories([]);
  };

  const autoPlay = storeSettings?.auto_image_carousel !== false;

  const filteredProducts = (selectedCategory
    ? products.filter(p => {
        if (p.category_id !== selectedCategory) return false;
        if (selectedSubCategories.length > 0 && !selectedSubCategories.includes(p.sub_category_id)) return false;
        return true;
      })
    : products
  ).sort((a, b) => {
    const aOos = isUnavailable(a, storeSettings) ? 1 : 0;
    const bOos = isUnavailable(b, storeSettings) ? 1 : 0;
    return aOos - bOos;
  });

  const handleAddToCart = (product, selectedVariant = null) => {
    if (!user) {
      navigate('/login');
      toast.info('Please login to add items to cart');
      return;
    }
    addItem(product, 1, selectedVariant);
    const label = selectedVariant ? `${product.name} (${selectedVariant.label})` : product.name;
    toast.success(`Added ${label} to cart`);
    trackEvent('add_to_cart', { product_id: product.id, product_name: product.name, variant: selectedVariant?.label });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const currencySymbol = storeSettings?.currency_symbol || '₹';

  // Get tomorrow's date for display
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <CustomerLayout title="Home">
      <div className="px-4 py-4 space-y-6">
        {/* Welcome Banner */}
        <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--theme-text-muted)]">
                {deliverySlots[0] ? `${deliverySlots[0].name} ${formatTime(deliverySlots[0].start_time)} - ${formatTime(deliverySlots[0].end_time)}` : 'Morning Delivery'}
              </p>
              <h2 className="font-heading text-xl font-bold text-[var(--theme-text-main)]">
                Hi {user?.name || 'there'}
              </h2>
            </div>
            <div className="text-right">
              <p className="text-xs text-[var(--theme-text-muted)]">Wallet</p>
              <p className="font-heading font-bold text-lg text-[var(--theme-primary)]">
                {currencySymbol}{(user?.wallet_balance || 0).toFixed(0)}
              </p>
            </div>
          </div>
        </div>

        {/* Promo Banners — prominent position */}
        {promoBanners.length > 0 && (
          <div className="animate-fade-in -mx-4">
            <PromoBannerCarousel banners={promoBanners} />
          </div>
        )}

        {/* Coupons */}
        {coupons.length > 0 && (
          <div className="animate-fade-in">
            <CouponStrip coupons={coupons} currencySymbol={currencySymbol} />
          </div>
        )}

        {/* Cut-off Time Banner */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 animate-fade-in">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-emerald-600" />
            <p className="text-sm text-emerald-800">
              Order by <strong>{storeSettings?.regular_cut_off_time || storeSettings?.cut_off_time || '11:59PM'}</strong> & We will deliver by <strong>{deliverySlots[0] ? formatTime(deliverySlots[0].end_time) : '7:30AM'}</strong>
            </p>
          </div>
        </div>

        {/* Tomorrow's Order */}
        <div className="flex items-center justify-between bg-[var(--theme-surface)] rounded-xl p-4 border border-black/5 animate-fade-in">
          <div className="flex items-center gap-3">
            <CalendarDays className="w-5 h-5 text-[var(--theme-text-muted)]" />
            <span className="text-[var(--theme-text-main)]">Order for {tomorrowStr}</span>
          </div>
          <Link to="/products" data-testid="add-item-btn">
            <Button size="sm" className="bg-[var(--theme-primary)] text-white rounded-full">
              Add Item
            </Button>
          </Link>
        </div>

        {/* Video Banners */}
        {socialData?.video_banners?.length > 0 && (
          <div className="animate-fade-in">
            <VideoBannerCarousel banners={socialData.video_banners} />
          </div>
        )}

        {/* Social Feeds */}
        {socialData?.show_instagram_feed && socialData?.instagram_handle && (
          <div className="animate-fade-in" data-testid="instagram-embed">
            <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)] flex items-center gap-2 mb-3">
              <Instagram className="w-4 h-4 text-pink-500" /> @{socialData.instagram_handle}
            </h3>
            <a href={`https://www.instagram.com/${socialData.instagram_handle}/`} target="_blank" rel="noreferrer"
              className="block bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 border border-pink-100 text-center hover:shadow-md transition-shadow">
              <Instagram className="w-8 h-8 text-pink-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">Follow us on Instagram</p>
              <p className="text-xs text-pink-500 mt-1">@{socialData.instagram_handle}</p>
            </a>
          </div>
        )}

        {/* Categories */}
        {categories.length > 0 && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg text-[var(--theme-text-main)]">Categories</h3>
              <Link to="/products" className="text-sm text-[var(--theme-primary)] font-medium flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
              <button
                onClick={() => handleSelectCategory(null)}
                className={`category-pill min-w-[80px] ${!selectedCategory ? 'active border-[var(--theme-primary)]' : ''}`}
                data-testid="category-all"
              >
                <Sparkles className="w-8 h-8 mb-1 text-[var(--theme-primary)]" />
                <span className="text-xs font-medium">All</span>
              </button>
              {categories.slice(0, 6).map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleSelectCategory(category.id)}
                  className={`category-pill min-w-[80px] ${selectedCategory === category.id ? 'active border-[var(--theme-primary)]' : ''}`}
                  data-testid={`category-${category.id}`}
                >
                  {category.image_url ? (
                    <img src={category.image_url} alt={category.name} className="w-10 h-10 rounded-lg object-cover mb-1" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[var(--theme-primary)]/10 flex items-center justify-center mb-1">
                      <span className="text-lg">{category.name.charAt(0)}</span>
                    </div>
                  )}
                  <span className="text-xs font-medium text-center line-clamp-2">{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Subcategory Filter */}
        <SubCategoryFilter
          categories={categories}
          selectedTab={selectedCategory}
          selectedSubCategories={selectedSubCategories}
          onSelect={setSelectedSubCategories}
        />

        {/* Products Grid */}
        <div className="animate-fade-in">
          <h3 className="font-heading font-bold text-lg text-[var(--theme-text-main)] mb-3">
            {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : 'Popular Products'}
          </h3>
          
          {loading ? (
            <div className={PRODUCT_GRID}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="product-card p-3 animate-pulse">
                  <div className="aspect-square bg-gray-200 rounded-xl mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[var(--theme-text-muted)]">No products available</p>
            </div>
          ) : (
            <div className={PRODUCT_GRID}>
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index}
                  currencySymbol={currencySymbol} storeSettings={storeSettings}
                  getItemQuantity={getItemQuantity} handleAddToCart={handleAddToCart}
                  updateQuantity={updateQuantity} autoPlay={autoPlay} />
              ))}
            </div>
          )}
        </div>

        {/* VIP Banner */}
        <div className="vip-banner animate-fade-in" data-testid="vip-banner">
          <div className="relative z-10">
            <h3 className="font-heading text-xl font-bold mb-1">JOIN VIP</h3>
            <p className="text-sm opacity-90 mb-3">Get exclusive discounts on subscriptions</p>
            <div className="flex items-center gap-2">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Money-back Guarantee</span>
            </div>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerHome;
